import React, { useContext, useEffect, useState } from "react";
import Checkicon from "../../assets/img/check-list.svg";
import notIcon from "../../assets/img/noticIcon.svg";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import { haveValue } from "../../helper/common";
import { toast } from "../../helper/common";
import TenantContext from "../../context/tenant/TenantContext";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { resolveFinancialDisplayError } from "../../Utils/bankStatementClassification";
import { Badge, Button, Icon } from "../design";

function ConnectBankCard(props) {
  const {
    setBankshow,
    paymentScoreLoading,
    setReconnectFlow,
    financial_info_errors,
    isGuarantor,
    isProfileOutdated = false,
    processing = false,
    bankStatementClassification = null,
    readOnly = false,
  } = props;

  const context = useContext(AuthContext);

  const user = props.user || context.user;

  const { _t } = useTranslator();
  // Non-blocking classification wins over the Algoan error when available;
  // when classification is unknown/missing we fall back to the Algoan error.
  const financialDisplayError = resolveFinancialDisplayError({
    classification: bankStatementClassification,
    financialInfoErrors: financial_info_errors,
    userFinancialInfoErrors: user?.financial_info_errors,
  });
  const hasFinancialError = haveValue(financialDisplayError.key);
  const { loadUserTenant } = useContext(AuthContext);
  const { sendRemoteConnectLink } = useContext(TenantContext);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);

  useEffect(() => {
    let intervalId;

    const checkAndLoad = () => {
      if (user?.bank_status === true && !haveValue(user?.paymentScores)) {
        loadUserTenant("tenant");
      } else {
        clearInterval(intervalId);
      }
    };

    checkAndLoad();
  }, []);

  const benefits = [
    _t(
      "createYourScore",
      "Benefit section in new tenant side to promote connection of bank account"
    ),
    _t(
      "fasterApprovalsFromLandlords",
      "Benefit section in new tenant side to promote connection of bank account"
    ),
    _t(
      "boostYourProfile",
      "Benefit section in new tenant side to promote connection of bank account"
    ),
    _t(
      "speedUpRenting",
      "Benefit section in new tenant side to promote connection of bank account"
    ),
    _t(
      "boostFinancials",
      "Benefit section in new tenant side to promote connection of bank account"
    ),
  ];

  const handleInitiateTink = () => {
    if (isProfileOutdated || processing) return;
    setBankshow(true);
    setReconnectFlow(true);
  };

  const outdatedTooltip = processing
    ? _t(
        "profileProcessingWait",
        "Your profile is still being processed. Please wait until it's ready."
      )
    : isProfileOutdated
      ? _t(
          "profileOutdatedRenewFirst",
          "Your profile is outdated. Please renew your profile first."
        )
      : undefined;
  const actionsBlocked = isProfileOutdated || processing;
  // View-only (tenant looking at a guarantor's profile): no bank actions.
  const isReadOnly = readOnly === true;

  const sendRemoteConnectToBank = async () => {
    if (actionsBlocked) return;
    setSendLinkLoading(true);

    try {
      const payload = {
        guarantor_id: user?._id,
      };
      const response = await sendRemoteConnectLink(payload);
      if (response?.success) {
        toast.success(_t("remainderSentSuccessfully"));
      }
    } catch (error) {
      console.log("Error", error);
      toast.error(_t("errorSendingTinkLink"));
    } finally {
      setSendLinkLoading(false);
    }
  };

  // New dark error UI (Image 1) applies ONLY to classification + generic
  // errors. Holder-mismatch keeps the legacy UI below.
  const useNewErrorUI =
    hasFinancialError && !financialDisplayError.isHolderMismatchError;

  const newErrorTitle = financialDisplayError.isClassificationError
    ? _t(
        financialDisplayError.titleKey || "wrong_document_title",
        financialDisplayError.titleFallback ||
          "This doesn't look like a bank statement"
      )
    : _t(
        "failedToVerifyFinancials",
        "Financial Skor error from algoan and tink"
      );

  const newErrorSubtitle = financialDisplayError.isClassificationError
    ? _t(
        financialDisplayError.subtitleKey || "wrong_document_subtitle",
        financialDisplayError.subtitleFallback ||
          "The file you uploaded isn't a bank statement. Try again with your account statement from the last 3 months."
      )
    : _t(financialDisplayError.key, financialDisplayError.fallback);

  if (useNewErrorUI) {
    return (
      <div className="conact-back-info h-100">
        <div className="bank-conact-wrapper bank-error-card">
          <div className="bank-error-top">
            <h4 className="bank-error-title mb-0">{newErrorTitle}</h4>
            <span className="bank-error-pill">
              <span className="bank-error-pill-icon" aria-hidden="true">
                !
              </span>
              {_t("actionRequired", "Action required")}
            </span>
          </div>
          <p className="bank-error-subtitle">{newErrorSubtitle}</p>
          {!isReadOnly && (
            <div className="bank-error-footer">
              <div
                style={{ display: "inline-block" }}
                data-tooltip-id={
                  actionsBlocked ? "reverify-blocked-tooltip" : undefined
                }
              >
                <Button
                  variant="primary"
                  size="md"
                  className="bank-error-retry"
                  onClick={handleInitiateTink}
                  disabled={actionsBlocked}
                  style={
                    actionsBlocked
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : undefined
                  }
                  title={outdatedTooltip}
                >
                  {_t("retry", "Retry")}
                  <img src="/images/arrow-right.svg" alt="" />
                </Button>
                <ReactTooltip
                  id="reverify-blocked-tooltip"
                  place="top"
                  className="react-tooltip-styling"
                  render={() =>
                    actionsBlocked ? <p>{outdatedTooltip}</p> : null
                  }
                />
              </div>
              <div className="bankIconWrapper bank-error-bankicon d-flex justify-content-center align-items-center">
                <img src="/images/bank-icon.svg" alt="" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="conact-back-info h-100">
      <div className="bank-conact-wrapper">
        <div className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start gap-4">
            <div>
              <h4
                className={`mb-0 ${hasFinancialError ? "failedKycWrapper" : ""}`}
              >
                {hasFinancialError ? (
                  <img src={notIcon} className="notIcon" />
                ) : (
                  (paymentScoreLoading || user?.bank_status == true) &&
                  !haveValue(user?.paymentScores) && (
                    <img
                      src="/gif/loading-gif.mov.gif"
                      className="loadingGif"
                    />
                  )
                )}
                {hasFinancialError
                  ? financialDisplayError.isClassificationError
                    ? _t(
                        financialDisplayError.titleKey ||
                          "wrong_document_title",
                        financialDisplayError.titleFallback ||
                          "This doesn't look like a bank statement"
                      )
                    : financialDisplayError.isHolderMismatchError
                      ? _t(
                          financialDisplayError.titleKey ||
                            "holder_mismatch_title",
                          financialDisplayError.titleFallback ||
                            "This account is not in your name"
                        )
                      : `${_t(
                          "failedToVerifyFinancials",
                          "Financial Skor error from algoan and tink"
                        )}`
                  : (paymentScoreLoading || user?.bank_status == true) &&
                      !haveValue(user?.paymentScores)
                    ? `${_t(
                        "yourSkorIsBeingGenerated",
                        "New tenant side screen connect bank component"
                      )}...`
                    : _t(
                        isGuarantor
                          ? "financialInformationNotYetVerified"
                          : "verifyYourFinancialInformation",
                        "New tenant side screen connect bank component"
                      )}
              </h4>
            </div>
            <div className="hideOnMobile">
              <Badge tone="lime" size="md">
                <Icon name="shieldCheck" size={16} />
                {_t("secured", "New tenant side screen connect bank component")}
              </Badge>
            </div>
          </div>
          {!hasFinancialError && (
            <p>
              {_t(
                "toBuildAStrongProfileConnectYourBankWithSkorNowItWillHelpYou",
                "New tenant side screen connect bank component"
              )}
            </p>
          )}
          <div className="hideOnDesktop">
            <Badge tone="lime" size="md">
              <Icon name="shieldCheck" size={16} />
              {_t("secured", "New tenant side screen connect bank component")}
            </Badge>
          </div>
          {hasFinancialError ? (
            <span className="failedKycError">
              {financialDisplayError.isClassificationError
                ? _t(
                    financialDisplayError.subtitleKey ||
                      "wrong_document_subtitle",
                    financialDisplayError.subtitleFallback ||
                      "The file you uploaded isn't a bank statement. Try again with your account statement from the last 3 months."
                  )
                : financialDisplayError.isHolderMismatchError
                  ? _t(
                      financialDisplayError.subtitleKey ||
                        "holder_mismatch_subtitle",
                      financialDisplayError.subtitleFallback ||
                        "The account holder doesn't match your verified identity. Try again with an account in your name."
                    )
                  : _t(
                      financialDisplayError.key,
                      financialDisplayError.fallback
                    )}
            </span>
          ) : (
            <ul>
              {benefits.map((benefit, index) => (
                <h6 key={index}>
                  <img src={Checkicon} alt="icon" />
                  <li>{benefit}</li>
                </h6>
              ))}
            </ul>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center custom-gap-2">
          {(isGuarantor || !isReadOnly) &&
            !paymentScoreLoading &&
            user?.bank_status === false &&
            !hasFinancialError &&
            (isGuarantor ? (
              <div
                style={{ display: "inline-block" }}
                data-tooltip-id={
                  actionsBlocked ? "verify-now-blocked-tooltip-g" : undefined
                }
              >
                <Button
                  variant="primary"
                  size="md"
                  loading={sendLinkLoading}
                  disabled={actionsBlocked}
                  onClick={sendRemoteConnectToBank}
                  style={
                    actionsBlocked
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : undefined
                  }
                  title={outdatedTooltip}
                >
                  {_t("sendRemainder")}
                  {!sendLinkLoading && <img src="/images/arrow-right.svg" />}
                </Button>
                <ReactTooltip
                  id="verify-now-blocked-tooltip-g"
                  place="top"
                  className="react-tooltip-styling"
                  render={() =>
                    actionsBlocked ? <p>{outdatedTooltip}</p> : null
                  }
                />
              </div>
            ) : (
              <div
                style={{ display: "inline-block" }}
                data-tooltip-id={
                  actionsBlocked ? "verify-now-blocked-tooltip" : undefined
                }
              >
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleInitiateTink}
                  disabled={actionsBlocked}
                  style={
                    actionsBlocked
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : undefined
                  }
                  title={outdatedTooltip}
                >
                  {_t(
                    "verifyNow",
                    "New tenant side screen connect bank component"
                  )}
                  <img src="/images/arrow-right.svg" />
                </Button>
                <ReactTooltip
                  id="verify-now-blocked-tooltip"
                  place="top"
                  className="react-tooltip-styling"
                  render={() =>
                    actionsBlocked ? <p>{outdatedTooltip}</p> : null
                  }
                />
              </div>
            ))}
          {hasFinancialError && !isReadOnly && (
            <div
              style={{ display: "inline-block" }}
              data-tooltip-id={
                actionsBlocked ? "reverify-blocked-tooltip" : undefined
              }
            >
              <Button
                variant="primary"
                size="md"
                onClick={handleInitiateTink}
                disabled={actionsBlocked}
                style={
                  actionsBlocked
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : undefined
                }
                title={outdatedTooltip}
              >
                {_t(
                  "reVerifyFinancials",
                  "New tenant side screen connect bank component"
                )}
                <img src="/images/arrow-right.svg" />
              </Button>
              <ReactTooltip
                id="reverify-blocked-tooltip"
                place="top"
                className="react-tooltip-styling"
                render={() =>
                  actionsBlocked ? <p>{outdatedTooltip}</p> : null
                }
              />
            </div>
          )}
          <div className="bankIconWrapper d-flex justify-content-center align-items-center">
            <img src="/images/bank-icon.svg" alt="" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConnectBankCard;
